<template>
  <b-modal id="deploy-confirm" size="md" centered hide-header>
    <div class="summary">
      <div class="reminder" v-if="shouldDisplayReminder">
        {{ $t("pageTemplateDeployReminder") }}
      </div>
      <div class="about-to">{{ aboutToText }}</div>
      <ul class="deploy-actions">
        <li class="deploy-action">{{ toUpdate }}</li>
        <li class="deploy-action">{{ toCreate }}</li>
        <li class="deploy-action">{{ toRemove }}</li>
      </ul>
    </div>

    <div class="confirm-row">{{ areYourSureText }}</div>

    <template v-slot:modal-footer>
      <button class="model-btn modal-cancel-btn" @click="hideModal()">
        {{ cancelText }}
      </button>
      <button class="model-btn modal-save-btn" @click="doDeploy()">
        {{ okText }}
      </button>
    </template>
  </b-modal>
</template>

<script>
  import { mapState } from "vuex";
  import services from "@/services";
  import { ACTION_LABELS } from "@/static/global-variables";

  function getTranslatedLabel(messages, records, action) {
    const mapping = {
      CREATE: "CREATE_X_RECORDS",
      REMOVE: "REMOVE_X_RECORDS",
      UPDATE: "UPDATE_X_RECORDS"
    };

    const count = records.filter(rec => {
      return rec.selectedAction === action;
    }).length;

    return messages[mapping[action]].Text_vod__c.replace("{0}", count);
  }

  function getPlatformValue(selectedPlatforms) {
    if (Array.isArray(selectedPlatforms) && selectedPlatforms.length > 0) {
      return selectedPlatforms.map(p => p.value).join(";");
    }

    return null;
  }

  function formatDeployments(toDeploy, fls) {
    return toDeploy.map(rec => {
      const output = {
        action: rec.selectedAction,
        actionId: rec.actionId,
        name: rec.Name,
        type: rec.type.devName,
        fields: []
      };

      if (rec.selectedAction !== ACTION_LABELS.create) {
        output.htmlReportId = rec.Id;
      }

      if (rec.selectedAction !== ACTION_LABELS.remove) {
        if (fls.hasProfileIdEditPermission) {
          output.fields.push({
            htmlReportField: "Profile_vod__c",
            value: rec.Profile_vod__c
          });
        }

        if (fls.hasProfileNameEditPermission) {
          output.fields.push({
            htmlReportField: "Profile_Name_vod__c",
            value: rec.Profile_Name_vod__c
          });
        }

        if (fls.hasObjectRecordTypeIdEditPermission) {
          output.fields.push({
            htmlReportField: "Object_RecordType_Id_vod__c",
            value: rec.Object_RecordType_Id_vod__c
          });
        }

        if (fls.hasObjectRecordTypeNameEditPermission) {
          output.fields.push({
            htmlReportField: "Object_RecordType_vod__c",
            value: rec.Object_RecordType_vod__c
          });
        }

        if (fls.hasPhoneEditPermission) {
          output.fields.push({
            htmlReportField: "Phone_vod__c",
            value: rec.dpPhone
          });
        }

        if (fls.hasPlatformEditPermission) {
          output.fields.push({
            htmlReportField: "Platform_vod__c",
            value: getPlatformValue(rec.selectedPlatforms)
          });
        }

        if (fls.hasExternalIdEditPermission) {
          output.fields.push({
            htmlReportField: "External_Id_vod__c",
            value: rec.External_Id_vod__c
          });
        }

        if (fls.hasVisibilityCriteriaEditPermission) {
          output.fields.push({
            htmlReportField: "Visibility_Criteria_vod__c",
            value: rec.Visibility_Criteria_vod__c
          });
        }

        if (fls.hasSharingGroupEditPermission) {
          output.fields.push({
            htmlReportField: "Sharing_Group_vod__c",
            value: rec.Sharing_Group_vod__c
          });
        }
      }

      return output;
    });
  }

  export default {
    name: "DeployConfirmModal",
    props: {
      appId: String,
      selectedVersion: Number,
      toDeploy: Array,
      hasPlatformEditPermission: Boolean,
      hasProfileIdEditPermission: Boolean,
      hasProfileNameEditPermission: Boolean,
      hasObjectRecordTypeIdEditPermission: Boolean,
      hasObjectRecordTypeNameEditPermission: Boolean,
      hasPhoneEditPermission: Boolean,
      hasExternalIdEditPermission: Boolean,
      hasVisibilityCriteriaEditPermission: Boolean,
      hasSharingGroupEditPermission: Boolean,
      isFromTemplate: Boolean
    },
    computed: {
      ...mapState({
        messages: "messages"
      }),
      aboutToText() {
        return this.getMessageText(this.messages.DEPLOYMENT_ABOUT_TO);
      },
      areYourSureText() {
        return this.getMessageText(this.messages.ARE_YOU_SURE);
      },
      cancelText() {
        return this.getMessageText(this.messages.CANCEL);
      },
      okText() {
        return this.getMessageText(this.messages.OK);
      },
      toUpdate() {
        return getTranslatedLabel(
          this.messages,
          this.toDeploy,
          ACTION_LABELS.update
        );
      },
      toCreate() {
        return getTranslatedLabel(
          this.messages,
          this.toDeploy,
          ACTION_LABELS.create
        );
      },
      toRemove() {
        return getTranslatedLabel(
          this.messages,
          this.toDeploy,
          ACTION_LABELS.remove
        );
      },
      shouldDisplayReminder() {
        const updateCount = this.toDeploy.filter(rec => {
          return rec.selectedAction === ACTION_LABELS.update;
        }).length;

        const createCount = this.toDeploy.filter(rec => {
          return rec.selectedAction === ACTION_LABELS.create;
        }).length;

        return this.isFromTemplate && (updateCount > 0 || createCount > 0);
      }
    },
    methods: {
      hideModal() {
        this.$bvModal.hide("deploy-confirm");
      },
      doDeploy() {
        this.$emit("on-deploy-records");
        const fls = {
          hasProfileIdEditPermission: this.hasProfileIdEditPermission,
          hasProfileNameEditPermission: this.hasProfileNameEditPermission,
          hasObjectRecordTypeIdEditPermission: this
            .hasObjectRecordTypeIdEditPermission,
          hasObjectRecordTypeNameEditPermission: this
            .hasObjectRecordTypeNameEditPermission,
          hasPlatformEditPermission: this.hasPlatformEditPermission,
          hasPhoneEditPermission: this.hasPhoneEditPermission,
          hasExternalIdEditPermission: this.hasExternalIdEditPermission,
          hasVisibilityCriteriaEditPermission: this
            .hasVisibilityCriteriaEditPermission,
          hasSharingGroupEditPermission: this.hasSharingGroupEditPermission
        };
        const deployList = formatDeployments(this.toDeploy, fls);

        services.doDeploy(this.appId, this.selectedVersion, deployList).then(
          resp => {
            this.$emit("done-deploy-records", resp, deployList);
          },
          err => {
            this.$emit("done-deploy-records", err.response, deployList);
          }
        );

        this.hideModal();
      }
    }
  };
</script>

<style scoped>
  .summary,
  .confirm-row {
    font-size: 1rem;
    font-weight: 400;
  }

  .model-btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    padding: 0;
    background: transparent;
    background-clip: border-box;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    line-height: 1.875rem;
    color: #0070d2;
    -webkit-appearance: none;
    white-space: normal;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    text-decoration: none;
  }

  .modal-save-btn {
    margin-left: 0.5rem;
    padding-left: 1rem;
    padding-right: 1rem;
    text-align: center;
    vertical-align: middle;
    justify-content: center;
    transition: border 0.15s linear;
    background-color: #0070d2;
    border: 1px solid #0070d2;
    color: #fff;
  }

  .modal-cancel-btn {
    padding-left: 1rem;
    padding-right: 1rem;
    text-align: center;
    vertical-align: middle;
    justify-content: center;
    transition: border 0.15s linear;
    border: 1px solid #dddbda;
    background-color: #fff;
  }

  .reminder {
    margin-bottom: 0.5rem;
    padding: 0 1rem;
    color: var(--TEXT_GREY);
  }
</style>
